import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  Box,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useStoreDispatch, useStoreSelector } from "../store/hooks";
import { selectIsCreating, startQuestionnaire, startQuestionnaireFromUrl } from "../store/questionnaireSlice";
import { useSearchParams, useNavigate } from "react-router-dom";
import { QuestionnaireBicycleType, QuestionnaireType } from "../data/AnswerEnums.ts";
import { SQToggleButton } from "../components/SQToggleButton";
import { useTranslation } from "react-i18next";
import { theme } from "../theme.ts";

export default function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useStoreDispatch();
  const isCreating = useStoreSelector(selectIsCreating);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const allSelectableTypes = Object.values(QuestionnaireType).filter(
      (type) => type !== QuestionnaireType.whole
  ) as QuestionnaireType[];

  const [selectedProductTypes, setSelectedProductTypes] = useState<QuestionnaireType[]>(
      []
  );
  const [showErrorModal, setShowErrorModal] = useState(false);

  const toggleProductType = (type: QuestionnaireType) => {
    setSelectedProductTypes((prev) =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  async function handleStartQuestionnaire(bicycleType?: QuestionnaireBicycleType) {
    try {
      const effectiveTypes = selectedProductTypes.length === allSelectableTypes.length
          ? [QuestionnaireType.whole]
          : selectedProductTypes;

      const newSearchParams = new URLSearchParams(searchParams);
      
      // Handle multiple questionnaire types in URL
      if (effectiveTypes.length === 1) {
        newSearchParams.set("questionnaireType", effectiveTypes[0]);
      } else {
        // For multiple types, use a comma-separated list
        newSearchParams.set("questionnaireTypes", effectiveTypes.join(','));
      }
      
      if (bicycleType) {
        newSearchParams.set("bicycleType", bicycleType);
      }
      setSearchParams(newSearchParams);

      await dispatch(
          startQuestionnaire({
            questionnaireTypes: effectiveTypes,
            bicycleType: bicycleType,
          })
      ).unwrap();

      navigate({
        pathname: "/questionnaire",
        search: window.location.search,
      });
    } catch (e) {
      console.error("Error while starting questionnaire: ", e);
      setShowErrorModal(true);
    }
  }

  useEffect(() => {
    const questionnaireTypeParam = searchParams.get("questionnaireType");
    const questionnaireTypesParam = searchParams.get("questionnaireTypes");
    const bicycleTypeParam = searchParams.get("bicycleType");

    if (questionnaireTypeParam || questionnaireTypesParam || bicycleTypeParam) {
      dispatch(startQuestionnaireFromUrl()).unwrap().then(() => {
        navigate({
          pathname: "/questionnaire",
          search: window.location.search,
        });
      }).catch((e) => {
        console.error("Error while starting questionnaire from URL: ", e);
        setShowErrorModal(true);
      });
    }
  }, [searchParams]);


  return (
      <Container sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: "85vh", py: 5 }}>
        <Stack alignItems="center" spacing={4}>
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              {t("home_choose_product_types")}
            </Typography>
            <Grid container spacing={1}>
              {allSelectableTypes.map((type) => (
                  <Grid item xs={6} md={4} key={type}>
                    <SQToggleButton
                        fullWidth
                        selected={selectedProductTypes.includes(type)}
                        onClick={() => toggleProductType(type)}
                    >
                      {t(`questionnaireType_${type}`)}
                    </SQToggleButton>
                  </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>

        <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="flex-end"
            alignItems="center"
            mt={4}
        >
          <Button
              variant="contained"
              onClick={() => handleStartQuestionnaire()}
              disabled={isCreating || selectedProductTypes.length === 0}
              sx={{ width: isMobile ? "100%" : "auto" }}
          >
            <Typography
                fontSize={theme.typography.button.fontSize}
                px={2}
            >
              {isCreating ? <CircularProgress size={20} color="inherit" /> : t("home_start_questionnaire")}
            </Typography>
          </Button>
        </Box>

        <Dialog open={showErrorModal} onClose={() => setShowErrorModal(false)}>
          <DialogTitle>{t("home_error_dialog_title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t("home_error_dialog_content")}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowErrorModal(false)} autoFocus>
              {t("home_error_dialog_ok")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
  );
}
